import { Trans } from "@lingui/react/macro";
import {
  BackgroundColor,
  Button,
  ButtonsContainer,
  classForBackground,
  ImageRatio
} from "@silversea-ssc/ui-components-react";
import React, { Suspense } from "react";
import * as styles from "./BlogCard.module.scss";
import clsx from "clsx";
import { WrapWithLink } from "components/WrapWithLink";
const ResponsiveImgixImage = React.lazy(
  () => import("components/ResponsiveImgixImage/ResponsiveImgixImage")
);

interface BlogCardProps {
  title: string;
  link: string;
  image: string;
  categories: string[];
  linkText: string;
  authors: string[];
  backgroundColor: BackgroundColor;
}

const BlogCard: React.FC<React.PropsWithChildren<BlogCardProps>> = ({
  title,
  link,
  image,
  categories,
  linkText,
  authors,
  backgroundColor = "neutral05"
}) => {
  let ctaLabel;
  if (linkText == "") {
    ctaLabel = <Trans id="cta-read-article">Read Article</Trans>;
  } else {
    ctaLabel = linkText;
  }
  const bgClass = classForBackground(backgroundColor);

  return (
    <article
      itemScope
      itemType="https://schema.org/Article"
      className={clsx(styles.card, bgClass)}
    >
      <meta itemProp="image" content={image} />
      <WrapWithLink
        className={styles.image}
        href={link}
        target="_blank"
        rel="nofollow noopener"
        data-content-piece="more_image"
        aria-hidden={true}
        tabIndex={-1}
        itemProp="url"
      >
        <ImageRatio ratio="card-compact" className={styles.imageInner}>
          <Suspense>
            <ResponsiveImgixImage
              image={{ src: image }}
              defaultWidth={315}
              defaultHeight={260}
              loading="lazy"
            />
          </Suspense>
        </ImageRatio>
      </WrapWithLink>
      <div className={styles.content}>
        <div className={styles.category}>{categories?.join(" - ")}</div>
        <h3 className={styles.title} itemProp="headline">
          <WrapWithLink
            href={link}
            data-content-piece="more_title"
            dangerouslySetInnerHTML={{ __html: title }}
          ></WrapWithLink>
        </h3>
        <div
          className={styles.author}
          itemScope
          itemType="https://schema.org/Person"
          itemProp="author"
        >
          <meta itemProp="name" content={authors?.[0]} />
          <Trans id="blog-by-author">by </Trans>
          <span> {authors?.join(" - ")}</span>
        </div>
        <div className={styles.ctaContainer}>
          <ButtonsContainer align="right">
            <Button
              renderAs="a"
              href={link}
              variant="text"
              iconName="arrow-right"
              target="_blank"
              rel="nofollow noopener"
              dataContentPiece="more_button"
            >
              {ctaLabel}
            </Button>
          </ButtonsContainer>
        </div>
      </div>
    </article>
  );
};

export default BlogCard;
